import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Product, ProductImage } from '@/types'

import Link from 'next/link'
import { ChevronRight, Hand, Truck } from 'lucide-react'
import { getProductBySlug, getRelatedProducts } from '@/lib/products'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import Reveal from '@/components/generals/reveal'
import ProductGallery from '@/components/shop/product-gallery'
import AddToCart from '@/components/shop/add-to-cart'
import ProductCard, { PRODUCT_FALLBACK_IMAGE } from '@/components/shop/product-card'
import { PRODUCT_TYPES, TYPE_DETAIL_IMAGES, productTypeLabel } from '@/constants/content'

interface ProductPageProps {
    product: Product
    related: Array<Product>
}

const galleryFor = (product: Product): Array<ProductImage> => {
    const main: ProductImage = { src: product.image || PRODUCT_FALLBACK_IMAGE, alt: product.imageAlt || product.name }
    const extra = product.gallery.length > 0 ? product.gallery : TYPE_DETAIL_IMAGES[product.type]
    return [main, ...extra]
}

const ProductPage: FC<ProductPageProps> = ({ product, related }): ReactNode => {
    const typeLabel = PRODUCT_TYPES.find(entry => entry.value === product.type)?.label ?? 'Paper'
    const ogImage = product.image?.startsWith('https://') ? product.image : undefined

    return (
        <Layout>
            <Seo
                title={product.name}
                description={product.tagline ?? `Handmade Samarkand silk paper from Qog'oz Atelier: ${product.name}.`}
                ogImage={ogImage}
            />

            <section id='product' aria-labelledby='product-title' className='container-page pb-20 pt-8 md:pb-28 md:pt-12'>
                <nav aria-label='Breadcrumb' className='mb-8 md:mb-12'>
                    <ol className='flex flex-wrap items-center gap-2 text-sm text-bark-soft'>
                        <li>
                            <Link href='/shop' className='ink-link pb-0.5 hover:text-bark'>Shop</Link>
                        </li>
                        <li aria-hidden='true'><ChevronRight className='size-3.5' /></li>
                        <li>
                            <Link href={`/shop?type=${product.type}`} className='ink-link pb-0.5 hover:text-bark'>{typeLabel}</Link>
                        </li>
                        <li aria-hidden='true'><ChevronRight className='size-3.5' /></li>
                        <li aria-current='page' className='text-bark'>
                            <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                        </li>
                    </ol>
                </nav>

                <div className='grid gap-12 lg:grid-cols-12 lg:gap-8'>
                    <div className='lg:col-span-7'>
                        <ProductGallery key={product.id} images={galleryFor(product)} name={product.name} />
                    </div>

                    <div className='lg:col-span-4 lg:col-start-9'>
                        <div className='lg:sticky lg:top-28'>
                            <p className='text-sm text-bark-soft'>
                                {productTypeLabel(product.type)}
                            </p>
                            <h1 id='product-title' className='mt-3 font-heading text-[clamp(2.2rem,4vw,3.3rem)] font-light leading-[1.02] tracking-[-0.02em] text-bark'>
                                <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                            </h1>
                            {product.tagline && (
                                <p data-mdlfy-cms={`products:${product.id}:tagline`} className='mt-4 font-heading text-[1.2rem] leading-snug text-bark-soft'>
                                    {product.tagline}
                                </p>
                            )}

                            <p className='mt-7 font-heading text-[2rem] font-light text-pomegranate'>
                                $<span>{product.price}</span>
                                <span className='ml-2 align-middle font-sans text-sm text-bark-soft'>USD, before shipping</span>
                            </p>

                            <dl className='mt-7 divide-y divide-bark/10 border-y border-bark/10 text-[0.95rem]'>
                                {product.sizeLabel && (
                                    <div className='flex justify-between gap-6 py-3'>
                                        <dt className='text-bark-soft'>Size</dt>
                                        <dd data-mdlfy-cms={`products:${product.id}:size_label`} className='text-right text-bark'>{product.sizeLabel}</dd>
                                    </div>
                                )}
                                {product.packLabel && (
                                    <div className='flex justify-between gap-6 py-3'>
                                        <dt className='text-bark-soft'>Contents</dt>
                                        <dd data-mdlfy-cms={`products:${product.id}:pack_label`} className='text-right text-bark'>{product.packLabel}</dd>
                                    </div>
                                )}
                            </dl>

                            <div className='mt-8'>
                                <AddToCart productId={product.id} price={product.price} available={product.available} />
                            </div>

                            <ul className='mt-8 space-y-4 text-sm text-bark-soft'>
                                <li className='flex gap-3'>
                                    <Truck aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-bark-soft' strokeWidth={1.75} />
                                    Tracked shipping worldwide from Samarkand, quoted on your invoice before you pay.
                                </li>
                                <li className='flex gap-3'>
                                    <Hand aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-bark-soft' strokeWidth={1.75} />
                                    Made by hand, so tone and thickness vary a little from piece to piece.
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section id='product-details' aria-labelledby='details-title' className='border-y border-bark/10 bg-paper-deep/50'>
                <div className='container-page grid gap-12 py-16 md:py-24 lg:grid-cols-12 lg:gap-8'>
                    <Reveal className='lg:col-span-3'>
                        <h2 id='details-title' className='font-heading text-[1.9rem] font-light text-bark'>
                            About this paper
                        </h2>
                    </Reveal>
                    <Reveal delay={100} className='lg:col-span-5'>
                        {product.description ? (
                            <p data-mdlfy-cms={`products:${product.id}:description`} className='whitespace-pre-line text-[1.06rem] leading-[1.8] text-bark'>
                                {product.description}
                            </p>
                        ) : (
                            <p className='text-[1.06rem] leading-[1.8] text-bark-soft'>
                                Formed at our vat from mulberry bark, dried on the wall and finished by hand in our courtyard near Samarkand.
                            </p>
                        )}
                    </Reveal>
                    {product.details.length > 0 && (
                        <Reveal delay={200} className='lg:col-span-3 lg:col-start-10'>
                            <h3 className='text-sm font-medium text-bark-soft'>The details</h3>
                            <ul className='mt-5 space-y-3'>
                                {product.details.map(detail => (
                                    <li key={detail} className='flex gap-3 text-[0.98rem] leading-snug text-bark'>
                                        <span aria-hidden='true' className='mt-2 size-1.5 shrink-0 rounded-full bg-pomegranate' />
                                        {detail}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    )}
                </div>
            </section>

            {related.length > 0 && (
                <section id='related' aria-labelledby='related-title' className='container-page py-20 md:py-28'>
                    <div className='flex flex-wrap items-end justify-between gap-6'>
                        <h2 id='related-title' className='font-heading text-[clamp(2rem,4vw,3rem)] font-light leading-tight text-bark'>
                            Also from the drying wall
                        </h2>
                        <Link href='/shop' className='font-medium text-pomegranate'>
                            <span className='ink-link pb-0.5'>Back to the shop</span>
                        </Link>
                    </div>
                    <ul className='mt-12 grid grid-cols-1 gap-x-8 gap-y-14 min-[480px]:grid-cols-2 lg:grid-cols-3 lg:gap-x-12'>
                        {related.map((item, index) => (
                            <li key={item.id} className={index === 1 ? 'lg:mt-14' : undefined}>
                                <Reveal delay={index * 120}>
                                    <ProductCard product={item} sizes='(min-width: 1024px) 30vw, (min-width: 480px) 46vw, 92vw' />
                                </Reveal>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    if (!slug) return { notFound: true }

    const product = await getProductBySlug(slug)
    if (!product) return { notFound: true }

    const related = await getRelatedProducts(product, 3).catch((error: unknown) => {
        console.error('Related products could not be loaded', error instanceof Error ? error.message : error)
        return []
    })

    return { props: { product, related } }
}

export default ProductPage
